<template>
  <Container :isTopSpace="isGlanceTable">
    <Dropdown :options="topicNames" :selectedOption="selectedTopic" :title="'Topics'" :action="setTopic"></Dropdown>
    <Dropdown
      v-if="selectedSubtopic && subtopicNames.length > 1"
      :options="subtopicNames"
      :title="'Subtopics'"
      :action="setSubtopic"
      :selectedOption="selectedSubtopic"
    ></Dropdown>
    <MultiDropdown
      v-if="!isGlanceTable"
      :options="regionValues"
      :title="title"
      :selectedOptions="selectedFilters"
      :last="true"
      :action="setRegions"
      :resetAction="resetRegions"
    ></MultiDropdown>
    <Dropdown
      v-if="isGlanceTable"
      :options="regionValues"
      :title="title"
      :selectedOption="selectedSingleFilter"
      :last="true"
      :action="setRegions"
      :resetAction="resetRegions"
      :isGlanceTable="isGlanceTable"
    ></Dropdown>
  </Container>
</template>

<script>
import { pipe, map, prop, path, pathOr, propEq, find, pathEq, uniq, last, equals, reject } from 'ramda';

import { Container } from './visualisationSwitcher.styles';
import { Dropdown, MultiDropdown } from '../dropdown';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { PREFIX as REGIONS_STORE } from '../../../modules/regions';
import {
  GET_TOPICS,
  GET_SELECTED_SUBTOPIC,
  GET_SELECTED_TOPIC,
  GET_SELECTED_FILTERS,
  GET_DATA,
} from '@/modules/regions/regions.getters';
import { FETCH_DATA } from '@/modules/regions/regions.actions';
import {
  SET_SELECTED_SUBTOPIC,
  SET_SELECTED_TOPIC,
  SET_LOADING,
  TOGGLE_SELECTED_FILTER,
  CLEAR_SELECTED_FILTERS,
} from '@/modules/regions/regions.mutations';
import { getRegionField } from '../../utils/helpers';
import { CHARTS } from '../../constants';
import { formatToKebabCase } from '@shared/utils/helpers';

export default {
  components: { Container, Dropdown, MultiDropdown },
  methods: {
    ...mapActions(REGIONS_STORE, [FETCH_DATA]),
    ...mapMutations(REGIONS_STORE, [
      SET_SELECTED_TOPIC,
      SET_LOADING,
      SET_SELECTED_SUBTOPIC,
      TOGGLE_SELECTED_FILTER,
      CLEAR_SELECTED_FILTERS,
    ]),
    setTopic(topic) {
      const formattedOption = formatToKebabCase(topic);
      const url = location.href.includes('data/')
        ? location.href.replace(/data\/.*/, `data/${formattedOption}`)
        : `${location.href}/${formattedOption}`;

      this[SET_LOADING](true);
      this[SET_SELECTED_TOPIC](topic);
      const dataSources = pipe(find(propEq('topic', topic)), prop('dataSources'))(this[GET_TOPICS]);
      const defaultSubtopic =
        find(pathEq(['meta', 'customData', 'defaultSubtopic'], 'true'), dataSources) || dataSources[0];
      this[SET_SELECTED_SUBTOPIC](defaultSubtopic);
      this[FETCH_DATA](defaultSubtopic.id);

      history.pushState({}, null, url);
    },
    setSubtopic(subtopic) {
      const formattedOption = formatToKebabCase(subtopic);
      const mathUrl = location.href.match(/.*data\/.*\//);
      const url = mathUrl ? `${mathUrl[0]}${formattedOption}` : `${location.href}/${formattedOption}`;
      const dataSources = pipe(find(propEq('topic', this.selectedTopic)), prop('dataSources'))(this[GET_TOPICS]);
      const selectedSubtopic = find(pathEq(['meta', 'customData', 'subtopic'], subtopic), dataSources);

      this[SET_LOADING](true);
      this[SET_SELECTED_SUBTOPIC](selectedSubtopic);
      this[FETCH_DATA](selectedSubtopic.id);

      history.pushState({}, null, url);
    },
    setRegions(region) {
      this[TOGGLE_SELECTED_FILTER](region);
    },
    resetRegions() {
      this[CLEAR_SELECTED_FILTERS]();
    },
  },
  computed: {
    ...mapGetters(REGIONS_STORE, [
      GET_TOPICS,
      GET_SELECTED_SUBTOPIC,
      GET_SELECTED_TOPIC,
      GET_SELECTED_FILTERS,
      GET_DATA,
    ]),
    selectedFilters: function() {
      return this[GET_SELECTED_FILTERS];
    },
    selectedSingleFilter: function() {
      const filter = last(this[GET_SELECTED_FILTERS]);

      return filter || `All ${this.title}`;
    },
    topicNames: function() {
      return map(prop('topic'), this[GET_TOPICS]);
    },
    subtopicNames: function() {
      return pipe(
        find(propEq('topic', this[GET_SELECTED_TOPIC])),
        prop('dataSources'),
        map(path(['meta', 'customData', 'subtopic']))
      )(this[GET_TOPICS]);
    },
    selectedTopic: function() {
      return this[GET_SELECTED_TOPIC];
    },
    regionValues: function() {
      const regionField = getRegionField(this[GET_SELECTED_SUBTOPIC]);
      const regions = pipe(map(prop(regionField)), uniq, reject(equals('Category')))(this[GET_DATA]);
      return regions.sort();
    },
    selectedSubtopic: function() {
      return pathOr(null, ['meta', 'customData', 'subtopic'], this[GET_SELECTED_SUBTOPIC]);
    },
    isGlanceTable: function() {
      const charts = pathOr([], ['tags', 'charts'], this[GET_SELECTED_SUBTOPIC]);

      return charts.includes(CHARTS.GLANCE_TABLE);
    },
    title: function() {
      const region = getRegionField(this[GET_SELECTED_SUBTOPIC]) || '';

      return region.includes('Country') && !region.includes('Country/Locale') ? 'Countries' : region + 's';
    },
  },
};
</script>
